import React, { useEffect } from 'react';

import { useJobProspectsState, useJobProspectsActions } from './JobProspectsContext';
import { getPreferredJobLocations, addPreferredJobLocation, removePreferredJobLocation } from './restApi';
import PageSectionTitle from '../../Shared/UI/PageSectionTitle/components/PageSectionTitle';
import Button from '../../Shared/UI/Button/components/Button';
import styles from './JobProspectsSettings.module.scss';

const US_STATES = [
  { code: 'AL', name: 'Alabama' },
  { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' },
  { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' },
  { code: 'DE', name: 'Delaware' },
  { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' },
  { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' },
  { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' },
  { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' },
  { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' },
  { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' },
  { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' },
  { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' },
  { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' },
  { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' },
  { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' },
  { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' },
  { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' },
  { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' },
  { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
];

const LocationFilterSettings = () => {
  const state = useJobProspectsState();
  const { onResponse, onError, setPreferredJobLocations, setAddingPreferredJobLocation, setSelectedStateCode } = useJobProspectsActions();

  const authenticityToken = document.querySelector("meta[name='csrf-token']")?.getAttribute("content");

  const preferredJobLocations = state?.preferredJobLocations || [];
  const addingPreferredJobLocation = state?.addingPreferredJobLocation || false;
  const selectedStateCode = state?.selectedStateCode || '';

  useEffect(() => {
    getPreferredJobLocations({
      onResponse: (data) => setPreferredJobLocations(data.preferredJobLocations || []),
      onError,
    });
  }, []);

  const existingCodes = preferredJobLocations.map(l => l.stateCode);
  const availableStates = US_STATES.filter(s => !existingCodes.includes(s.code));

  const handleAdd = () => {
    if (!selectedStateCode) return;

    const stateObj = US_STATES.find(s => s.code === selectedStateCode);
    if (!stateObj) return;

    setAddingPreferredJobLocation(true);
    addPreferredJobLocation({
      body: { stateCode: stateObj.code, displayName: stateObj.name, authenticityToken },
      onResponse,
      onError: (data) => onError({ ...data, addingPreferredJobLocation: false }),
    });
  };

  const handleRemove = (location) => {
    removePreferredJobLocation({
      id: location.id,
      body: { authenticityToken },
      onResponse,
      onError,
    });
  };

  return (
    <div className={styles.locationFilterCard}>
      <PageSectionTitle text="Preferred Job Locations" />
      <p className={styles.description}>
        Only show job recommendations from these states. Remote jobs are always included.
        Leave empty to receive all recommendations regardless of location.
      </p>

      {preferredJobLocations.length > 0 && (
        <div className={styles.locationChips}>
          {preferredJobLocations.map(location => (
            <div key={location.id} className={styles.locationChip}>
              <span>{location.displayName}</span>
              <button
                className={styles.locationChipRemove}
                onClick={() => handleRemove(location)}
                aria-label={`Remove ${location.displayName}`}
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {preferredJobLocations.length === 0 && (
        <p className={styles.noLocationsNote}>No preferred locations set — all job recommendations are shown.</p>
      )}

      {availableStates.length > 0 && (
        <div className={styles.locationFilterRow}>
          <select
            className={styles.locationFilterSelect}
            value={selectedStateCode}
            onChange={e => setSelectedStateCode(e.target.value)}
          >
            <option value="">Select a state...</option>
            {availableStates.map(s => (
              <option key={s.code} value={s.code}>{s.name}</option>
            ))}
          </select>
          <Button variant="primary" onClick={handleAdd} disabled={!selectedStateCode || addingPreferredJobLocation}>
            {addingPreferredJobLocation ? 'Adding...' : 'Add'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default LocationFilterSettings;
